// src/index.ts
var owners = /* @__PURE__ */ new WeakMap();
var eventTypes = ["ready", "statechange", "locationclick", "locationhover", "flyend", "reducedmotionchange"];
var defaultLoader = async () => import("./interactive-globe-R25754OQ.js");
function createGlobeController(options = {}) {
  const owner = {};
  let generation = 0;
  let engine = null;
  let container = null;
  let config = null;
  let fallback = null;
  let motion = null;
  let fallbackVisible = true;
  let removeContextListener = null;
  let forcedReduced = false;
  let staticRequested = false;
  const eventListeners = /* @__PURE__ */ new Map();
  let removeEngineEvents = [];
  const layerFactories = /* @__PURE__ */ new Set();
  let liveLayers = /* @__PURE__ */ new Map();
  let removeFrameHook = null;
  function mountLayer(factory) {
    if (!engine || liveLayers.has(factory)) return;
    try {
      liveLayers.set(factory, factory({ scene: engine.scene, globe: engine.globe, renderer: engine.renderer }));
    } catch {
      console.warn("GLOBE_LAYER_MOUNT_FAILED");
    }
  }
  function hookFrames(target) {
    const scene = target.scene;
    const previous = scene.onBeforeRender;
    let last = performance.now();
    const hook = (...args) => {
      previous.apply(scene, args);
      const now = performance.now();
      const seconds = Math.min(0.1, (now - last) / 1e3);
      last = now;
      for (const layer of liveLayers.values()) {
        try {
          layer.update?.(seconds, args[2]);
        } catch {
          console.warn("GLOBE_LAYER_UPDATE_FAILED");
        }
      }
    };
    scene.onBeforeRender = hook;
    removeFrameHook = () => {
      if (scene.onBeforeRender === hook) scene.onBeforeRender = previous;
    };
  }
  function disposeLayers() {
    for (const layer of liveLayers.values()) {
      try {
        layer.dispose();
      } catch {
        console.warn("GLOBE_LAYER_DISPOSE_FAILED");
      }
    }
    liveLayers = /* @__PURE__ */ new Map();
    removeFrameHook?.();
    removeFrameHook = null;
  }
  let snapshot = Object.freeze({ phase: "UNMOUNTED", reason: null });
  function publish(phase, reason = null) {
    snapshot = Object.freeze({ phase, reason });
    try {
      options.onState?.(snapshot);
    } catch {
      console.warn("GLOBE_STATE_OBSERVER_FAILED");
    }
  }
  function disposeEngine() {
    disposeLayers();
    removeContextListener?.();
    removeContextListener = null;
    for (const unsubscribe of removeEngineEvents) unsubscribe();
    removeEngineEvents = [];
    engine?.destroy();
    engine = null;
  }
  function removeFallback() {
    fallback?.remove();
    fallback = null;
  }
  function textualFallback(visible = true) {
    if (!container || !config) return;
    removeFallback();
    fallbackVisible = visible;
    const document = container.ownerDocument;
    fallback = document.createElement("section");
    fallback.className = visible ? "" : "ig-a11y";
    fallback.dataset.globeFallback = "true";
    fallback.setAttribute("aria-label", config.accessibility.title);
    const title = document.createElement("h2");
    title.textContent = config.accessibility.title;
    fallback.append(title);
    const description = document.createElement("p");
    description.textContent = config.accessibility.description;
    fallback.append(description);
    const list = document.createElement("ul");
    const locations = new Map((config.locations ?? []).map((location) => [location.id, location.label]));
    for (const route of config.routes ?? []) {
      const item = document.createElement("li");
      item.textContent = `${locations.get(route.from) ?? route.from} \u2192 ${locations.get(route.to) ?? route.to}${route.label ? `: ${route.label}` : ""}`;
      list.append(item);
    }
    fallback.append(list);
    container.append(fallback);
  }
  function reduced() {
    return forcedReduced || config?.reducedMotion === "always" || motion?.matches === true;
  }
  function stopMount() {
    generation++;
    disposeEngine();
    removeFallback();
    motion?.removeEventListener("change", onMotion);
    motion = null;
    if (container && owners.get(container) === owner) owners.delete(container);
    container = null;
    config = null;
  }
  async function initialize() {
    const version = ++generation;
    disposeEngine();
    removeFallback();
    if (!container || !config) return snapshot;
    if (staticRequested || reduced()) {
      textualFallback();
      publish("STATIC", reduced() ? "REDUCED_MOTION" : "STATIC_REQUESTED");
      return snapshot;
    }
    publish("LOADING");
    const target = container;
    const input = config;
    const before = new Set(Array.from(target.children));
    try {
      const module = await (options.load ?? defaultLoader)();
      if (version !== generation || container !== target) return snapshot;
      engine = module.createInteractiveGlobe({
        ...input,
        container: target,
        reducedMotion: "auto",
        interaction: { ...input.interaction, zoom: input.interaction?.zoom ?? false, touchAction: input.interaction?.touchAction ?? "pan-y" },
        performance: { ...input.performance, pauseWhenOffscreen: true }
      });
      for (const type of eventTypes) removeEngineEvents.push(engine.on(type, (payload) => {
        for (const callback of eventListeners.get(type) ?? []) {
          try {
            callback(payload);
          } catch {
            console.warn("GLOBE_EVENT_OBSERVER_FAILED");
          }
        }
      }));
      engine.root.querySelector(".ig-a11y")?.setAttribute("aria-hidden", "true");
      engine.root.removeAttribute("role");
      engine.root.removeAttribute("aria-labelledby");
      engine.root.removeAttribute("aria-describedby");
      engine.root.setAttribute("aria-label", input.accessibility.title);
      textualFallback(input.accessibility.visible === true);
      const canvas = engine.canvas;
      const lost = () => {
        if (version !== generation) return;
        generation++;
        disposeEngine();
        textualFallback();
        publish("FALLBACK", "CONTEXT_LOST");
      };
      canvas.addEventListener("webglcontextlost", lost);
      removeContextListener = () => canvas.removeEventListener("webglcontextlost", lost);
      hookFrames(engine);
      for (const factory of layerFactories) mountLayer(factory);
      publish(input.autoStart === false ? "PAUSED" : "READY");
    } catch {
      if (version !== generation) return snapshot;
      disposeEngine();
      for (const child of Array.from(target.children)) if (!before.has(child) && child.classList.contains("ig-root")) child.remove();
      textualFallback();
      publish("FALLBACK", "WEBGL_OR_RUNTIME_UNAVAILABLE");
    }
    return snapshot;
  }
  function onMotion() {
    void initialize();
  }
  return {
    getSnapshot: () => snapshot,
    on(type, callback) {
      const callbacks = eventListeners.get(type) ?? /* @__PURE__ */ new Set();
      eventListeners.set(type, callbacks);
      const erased = callback;
      callbacks.add(erased);
      return () => {
        callbacks.delete(erased);
      };
    },
    async mount(target, input, mode = "WEBGL") {
      if (snapshot.phase === "DESTROYED") throw new Error("GLOBE_CONTROLLER_DESTROYED");
      if (owners.has(target) && owners.get(target) !== owner) throw new Error("GLOBE_CONTAINER_ALREADY_MOUNTED");
      if (!target.isConnected || target.clientWidth <= 0 || target.clientHeight <= 0) throw new Error("GLOBE_CONTAINER_NOT_MEASURABLE");
      if (!input.accessibility?.title || !input.accessibility.description) throw new Error("GLOBE_ACCESSIBLE_CONTENT_REQUIRED");
      stopMount();
      container = target;
      config = structuredClone(input);
      staticRequested = mode === "STATIC";
      owners.set(target, owner);
      motion = target.ownerDocument.defaultView?.matchMedia("(prefers-reduced-motion: reduce)") ?? null;
      motion?.addEventListener("change", onMotion);
      return initialize();
    },
    pause() {
      if (engine) {
        engine.stop();
        publish("PAUSED");
      }
    },
    resume() {
      if (engine && !reduced() && !staticRequested) {
        engine.start();
        publish("READY");
      }
    },
    resize() {
      engine?.resize();
    },
    setScrollProgress(progress, frames) {
      if (!Number.isFinite(progress)) throw new Error("INVALID_GLOBE_PROGRESS");
      engine?.setProgress(Math.min(1, Math.max(0, progress)), frames ? structuredClone(frames) : void 0);
    },
    getView() {
      return engine ? engine.getView() : null;
    },
    releaseProgress() {
      engine?.releaseProgress();
    },
    setData(data) {
      if (!config) return;
      config = { ...config, ...structuredClone(data) };
      engine?.setData(data, { animate: !reduced() });
      if (fallback) textualFallback(fallbackVisible);
    },
    async setReducedMotion(enabled) {
      forcedReduced = enabled;
      return initialize();
    },
    // Narrative passthroughs: consumer-owned scenes drive camera/route/theme; all are no-ops without a live engine.
    flyTo(view, flight = {}) {
      if (![view.lat, view.lng].every(Number.isFinite)) throw new Error("INVALID_GLOBE_VIEW");
      engine?.flyTo(view, reduced() ? { ...flight, duration: 1 } : flight);
    },
    showRoutes(ids, reveal = {}) {
      engine?.showRoutes(ids, { ...reveal, animate: !reduced() });
    },
    hideRoutes(ids) {
      engine?.hideRoutes(ids, { animate: !reduced() });
    },
    setGlobeScale(scale, immediate = false) {
      if (!Number.isFinite(scale) || scale <= 0) throw new Error("INVALID_GLOBE_SCALE");
      engine?.setGlobeScale(scale, immediate || reduced());
    },
    setOpacity(opacity) {
      if (!Number.isFinite(opacity)) throw new Error("INVALID_GLOBE_OPACITY");
      engine?.setOpacity(Math.min(1, Math.max(0, opacity)));
    },
    setAutoRotate(enabled) {
      engine?.setAutoRotate(enabled && !reduced());
    },
    setTheme(theme) {
      engine?.setTheme(structuredClone(theme));
    },
    projectLocation(id) {
      return engine ? engine.projectLocation(id) : null;
    },
    /** Mounts a consumer 3D overlay now (if live) and on every future (re)mount; returns a detach function. */
    attachLayer(factory) {
      layerFactories.add(factory);
      mountLayer(factory);
      return () => {
        layerFactories.delete(factory);
        const layer = liveLayers.get(factory);
        liveLayers.delete(factory);
        try {
          layer?.dispose();
        } catch {
          console.warn("GLOBE_LAYER_DISPOSE_FAILED");
        }
      };
    },
    diagnostics() {
      if (!engine) return null;
      const { render, memory, programs } = engine.renderer.info;
      return Object.freeze({
        running: engine.running,
        drawCalls: render.calls,
        triangles: render.triangles,
        points: render.points,
        frames: render.frame,
        geometries: memory.geometries,
        textures: memory.textures,
        programs: programs?.length ?? 0,
        canvasWidth: engine.canvas.width,
        canvasHeight: engine.canvas.height
      });
    },
    unmount() {
      stopMount();
      if (snapshot.phase !== "DESTROYED") publish("UNMOUNTED");
    },
    destroy() {
      if (snapshot.phase === "DESTROYED") return;
      stopMount();
      eventListeners.clear();
      layerFactories.clear();
      publish("DESTROYED");
    }
  };
}

export {
  createGlobeController
};
//# sourceMappingURL=chunk-I4SCARLV.js.map
